import { cloneElement, isValidElement, useContext, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import type { Section, PageTheme, SectionTextSize } from '../model/pages';
import { formBgCss, readableOn, formAccentFor, deadlineTime } from '../model/pages';
import type { StyleClasses } from './styles';
import EmbedBlock from './EmbedBlock';
import CrmFormFrame, { BookingFrame, type FormDone } from './CrmFormFrame';
import FormMock, { type PreviewForm } from './FormMock';
import EditableText, { EmptyField } from './EditableText';
import { EditorialMotif } from './EditorialDecor';
import { TEXT_SIZE, ROOMY_SIZE, Marked, Stars, AvatarStack, fieldPlaceholder, type SectionKit, type TextKey } from './sections/kit';
import PageIcon, { hasPageIcon } from './sections/icons';
import VideoPoster from './sections/VideoPoster';
import NavBlock from './sections/NavBlock';
import BannerBlock from './sections/BannerBlock';
import TestimonialsBlock from './sections/TestimonialsBlock';
import ModulesBlock from './sections/ModulesBlock';
import BioBlock from './sections/BioBlock';
import OfferBlock from './sections/OfferBlock';
import { SsrContext } from './ssr-context';
import { normalizeSectionDesign } from '../model/section-design';
import { sectionDesignClasses, sectionGridProps } from './section-design';

/** The CRM form a page links to, as the page renders it. */
export interface PageFormLink { slug: string; name?: string }

interface Props {
  section: Section;
  s: StyleClasses;
  theme: PageTheme;
  /** The page's CRM form (a live frame of /f/<slug>), or none. */
  form?: PageFormLink | null;
  /** The page id, sent with a lead so it counts for this page. */
  pageId?: string;
  /** No real form (galleries, template previews): a still of the form a
   *  template will create (FormMock). */
  previewForm?: PreviewForm | null;
  /** The form opens in preview mode (preview=1): staff answers are checked but
   *  never saved as a lead (editor canvas, ?pt= page previews). */
  formPreview?: boolean;
  /** A form on this section was sent (see FormDone). */
  onFormDone?: (done: FormDone) => void;
  /** A call was booked in a form's calendar or a booking section. */
  onBooked?: () => void;
  /** A read-only render (thumbnails, galleries): videos stay stills and the
   *  form frame takes no clicks. */
  preview?: boolean;
  onCtaClick?: (label: string) => void;
  /** Multi-step pages: CTAs with href '#next' call this instead of navigating. */
  onNextStep?: () => void;
  /** Popup form mode: when 'popup', a CTA/pricing button pointing to '#form' and
   *  the Form section's trigger open the modal via onOpenForm instead of scrolling
   *  to an inline form. Absent/'inline' = today's inline behavior. */
  formMode?: 'inline' | 'popup';
  onOpenForm?: () => void;
  /** Editor canvas, SELECTED section only: the primary text fields render as
   *  contentEditable (EditableText) and commit patches here on blur. Absent on
   *  public pages and unselected sections — the render stays byte-identical. */
  onEditText?: (patch: Partial<Section>) => void;
  /** Editor canvas: a config section with no content yet (video/embed/booking/
   *  empty logos) renders a labeled placeholder instead of nothing, so a freshly
   *  dropped block is visible. Never set on public pages. */
  editable?: boolean;
  /** Editor "Mobile" preview: force the two-column layouts (side hero, image+
   *  text split, split form) to stack into a single column in React, instead of
   *  relying on the `md:` viewport breakpoint. The preview canvas lives in an
   *  iframe and some browsers (notably iOS Safari) don't honor a viewport on a
   *  dynamically-created iframe, so the breakpoint can mis-fire; this makes the
   *  mobile preview deterministic. Absent on public pages (they use real
   *  breakpoints and render byte-identically). */
  stack?: boolean;
  /** Accent override for a brand-color band (styles.ts bandTone): buttons and
   *  accents use this ink, and button text uses the brand color, so nothing
   *  brand-colored disappears into the band. Absent everywhere else. */
  accent?: string;
}

/** A form card's frame classes without their padding (and without repeats): a
 *  CRM form (and its still) fills the card edge to edge, with its own inner
 *  spacing. Pure. */
export function flushFrame(cls: string): string {
  const out: string[] = [];
  for (const c of cls.split(/\s+/)) {
    if (c && !/^(?:[a-z-]+:)*p[trblxy]?-\S+$/.test(c) && !out.includes(c)) out.push(c);
  }
  return out.join(' ');
}

/** Inline check mark used by the modern list variants. */
const Check = ({ color, size = 18, className = '' }: { color: string; size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" className={`flex-none ${className}`} aria-hidden><path d="M20 6 9 17l-5-5" /></svg>
);

export default function SectionRenderer({ section, s: baseStyle, theme, form, pageId, previewForm, formPreview, onFormDone, onBooked, preview, onCtaClick, onNextStep, onEditText, formMode, onOpenForm, editable, stack, accent }: Props) {
  const forwarding = section.forward_attribution === true ? 'true' : undefined;
  const design = normalizeSectionDesign(section.design);
  const s = sectionDesignClasses(baseStyle, design);
  const gridProps = sectionGridProps(design, stack);
  // An explicit alignment overrides the preset's text AND flex/grid placement.
  // Undefined leaves every legacy class and layout intact.
  const alignment = section.align === 'left' || section.align === 'center' || section.align === 'right' ? section.align : undefined;
  const edge = alignment === 'left' ? 'start' : alignment === 'right' ? 'end' : 'center';
  const alignText: CSSProperties | undefined = alignment ? { textAlign: alignment } : undefined;
  const alignFlex: CSSProperties | undefined = alignment ? { justifyContent: alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center' } : undefined;
  const alignGrid: CSSProperties | undefined = alignment ? { ...alignText, justifyItems: edge } : undefined;
  const alignSelf: CSSProperties | undefined = alignment ? { justifySelf: edge } : undefined;
  const alignMargin: CSSProperties | undefined = alignment ? { marginLeft: alignment === 'left' ? 0 : 'auto', marginRight: alignment === 'right' ? 0 : 'auto' } : undefined;
  const alignBox: CSSProperties | undefined = alignment ? { ...alignText, ...alignMargin } : undefined;
  // Override the content's own measure only when a width was explicitly chosen.
  const widthNode = (node: ReactNode): ReactNode => design?.content_width && isValidElement<{ style?: CSSProperties }>(node) && typeof node.type === 'string'
    ? cloneElement(node, { style: { ...node.props.style, maxWidth: 'none', width: '100%' } }) : node;
  // Two-up grid classes for the side/split layouts — dropped when `stack` (the
  // editor's Mobile preview) so they render single-column without depending on
  // the iframe's media-query viewport.
  const twoCol = stack ? '' : 'md:grid-cols-2';
  const ord = (c: string) => (stack ? '' : c);
  const brandColor = theme.brand_color || '#4f46e5';
  const brand = accent ?? brandColor;
  const onBrand = accent ? brandColor : readableOn(brand); // legible text/icon color on top of the brand fill
  // Button fill: the theme's button color when set (else the brand). Inside a
  // brand band the accent override wins, so buttons stay visible.
  const btnBg = design?.button_color ?? accent ?? theme.button_color ?? brand;
  const onBtn = design?.button_color ? readableOn(btnBg) : accent ? brandColor : readableOn(btnBg);
  const size: SectionTextSize = section.text_size ?? 'md';
  // Modern (roomy) styles carry a larger display scale for the headline keys;
  // classic styles keep TEXT_SIZE exactly.
  const tz = (k: TextKey) => (s.roomy && k in ROOMY_SIZE ? ROOMY_SIZE[k as keyof typeof ROOMY_SIZE][size] : TEXT_SIZE[k][size]);
  const popup = formMode === 'popup';
  const editorial = theme.style === 'editorial';
  // A button href that should open the popup form (instead of scrolling) when in
  // popup mode. Used by CTA and pricing buttons.
  // Only when there is a form to show: with none, '#form' stays a plain anchor.
  const opensPopup = (href: string | undefined) => popup && href === '#form' && (!!form || !!previewForm);

  /** A text field's render: the raw string on public pages (exactly as before),
   *  or an inline editor when this section is selected on the Visual canvas.
   *  On the canvas an empty field shows a placeholder, the same one whether or
   *  not its section is selected. `patch` turns the committed text into the
   *  section patch to apply (and names the field, for its placeholder). */
  const txt = (value: string, patch: (text: string) => Partial<Section>, multiline?: boolean, placeholder?: string): React.ReactNode =>
    onEditText
      ? <EditableText value={value} multiline={multiline} placeholder={placeholder ?? fieldPlaceholder(patch)} onCommit={(text) => onEditText(patch(text))} />
      : editable && !value ? <EmptyField placeholder={placeholder ?? fieldPlaceholder(patch)} /> : value;

  // Highlighted heading words (section.highlight). With no highlight (every old
  // section) Marked returns the raw string, so the render is unchanged. The
  // inline editor shows the same highlighted output until its text is focused,
  // so selecting a section never re-wraps the headline.
  const markStyle = section.mark ?? s.mark ?? 'color';
  const markFill = s.markColor ?? (markStyle === 'marker' ? `${brand}55` : brand);
  const hl = (value: string, patch: (text: string) => Partial<Section>, placeholder?: string): React.ReactNode => {
    const marked = <Marked text={value} highlight={section.highlight} mark={markStyle} color={markFill} accent={brand} />;
    if (onEditText) return <EditableText value={value} display={marked} placeholder={placeholder ?? fieldPlaceholder(patch)} onCommit={(text) => onEditText(patch(text))} />;
    if (editable && !value) return <EmptyField placeholder={placeholder ?? fieldPlaceholder(patch)} />;
    return marked;
  };

  // Eyebrow label: the style's own treatment when it defines one (modern), else
  // the classic uppercase pill.
  const eyebrowCls = s.eyebrow ?? `rounded-full px-3 py-1 font-semibold uppercase tracking-wide ${s.badge}`;

  // A link/button with the shared click semantics ('#next' advances, popup
  // '#form' opens the modal, external links open a new tab).
  const link = (label: string, href: string, className: string, style?: CSSProperties, children?: ReactNode): ReactNode => (
    <a href={href} data-nla-attribution={forwarding} target={href.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
      onClick={(e) => { onCtaClick?.(label); if (href === '#next') { e.preventDefault(); onNextStep?.(); } else if (opensPopup(href)) { e.preventDefault(); onOpenForm?.(); } }}
      className={className} style={style}>{children ?? label}</a>
  );

  // Social proof line (avatar stack + stars + a short line) and the small
  // reassurance note under a button. Both render only when their fields are set.
  const proofLine = (center: boolean, onImg?: boolean): ReactNode => {
    const avatars = (section.proof_avatars ?? []).filter(Boolean);
    if (!section.proof_text && !avatars.length && !section.rating) return null;
    return (
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 pt-1 ${center ? 'justify-center' : ''}`} style={alignFlex}>
        {avatars.length > 0 && <AvatarStack urls={avatars} size={34} ring="var(--band-bg, #ffffff)" />}
        <div className={`grid gap-0.5 ${center && !avatars.length ? 'justify-items-center text-center' : 'text-left'}`} style={alignGrid}>
          {!!section.rating && <Stars n={section.rating} color={s.star} size={15} />}
          {section.proof_text && <span className={`text-sm ${onImg ? 'text-white/80' : s.label ?? s.sub}`}>{txt(section.proof_text, (t) => ({ proof_text: t }))}</span>}
        </div>
      </div>
    );
  };
  const ctaNote = (center: boolean, onImg?: boolean): ReactNode => section.cta_note
    ? <p className={`text-xs ${center ? 'text-center' : ''} ${onImg ? 'text-white/70' : s.label ?? s.sub}`} style={alignText}>{txt(section.cta_note, (t) => ({ cta_note: t }))}</p>
    : null;

  // Optional centered section header (eyebrow + heading + subheadline) for the
  // collection sections (features, stats, pricing, faq, steps, gallery, team,
  // logos, testimonial). Renders only when one of those fields is set, so pages
  // that don't set them are unchanged. Hero/text/split/guarantee use these
  // fields as their OWN content and never call this — that's what makes a grid
  // read as a real, titled landing-page section.
  const sectionHead = (section.eyebrow || section.heading || section.subheadline) ? (
    <div className={s.roomy ? 'mx-auto mb-10 grid max-w-3xl gap-4 text-center sm:mb-14' : 'mx-auto mb-10 grid max-w-2xl gap-3 text-center'} style={alignBox}>
      {section.eyebrow && (
        <span className={s.eyebrow ? `mx-auto ${tz('eyebrow')} ${s.eyebrow}` : `mx-auto rounded-full px-3 py-1 ${tz('eyebrow')} font-semibold uppercase tracking-wide ${s.badge}`} style={alignMargin}>
          {txt(section.eyebrow, (t) => ({ eyebrow: t }))}
        </span>
      )}
      {section.heading && <h2 className={`${tz('heading')} ${s.heading}`}>{hl(section.heading, (t) => ({ heading: t }))}</h2>}
      {section.subheadline && <p className={`${tz('hero_sub')} ${s.sub}`}>{txt(section.subheadline, (t) => ({ subheadline: t }), true)}</p>}
    </div>
  ) : null;
  // Wrap a section's body with the header when present. Returns null when the body
  // is empty (a header alone renders nothing).
  const withHead = (node: React.ReactNode): React.ReactNode => node ? (sectionHead ? <div>{sectionHead}{widthNode(node)}</div> : widthNode(node)) : null;

  // Editor-only placeholder for a config section that has no content yet (so a
  // freshly dropped block is visible + obviously needs input). Null on public pages.
  const ph = (title: string, hint: string): React.ReactNode => editable ? (
    <div className={`mx-auto grid max-w-md place-items-center gap-1 rounded-2xl border-2 border-dashed p-10 text-center ${s.divider}`}>
      <span className={`text-sm font-semibold ${s.heading}`}>{title}</span>
      <span className={`text-xs ${s.sub}`}>{hint}</span>
    </div>
  ) : null;

  // The kit handed to the funnel blocks that live in ./sections.
  const kit = (): SectionKit => ({
    section, s, theme, brand, onBrand, btnBg, onBtn, tz, txt, hl, head: sectionHead, withHead, link, ph, eyebrowCls,
    stack, editing: !!onEditText, editable: !!editable,
    contentStyle: design?.content_width ? { maxWidth: 'none', width: '100%' } : undefined,
  });

  const renderSection = (): ReactNode => {
  switch (section.type) {
    case 'hero': {
      // A video (VSL) takes the focal spot and suppresses the side-image layout.
      // A poster frame (poster_url) is a video too, but it may sit on the side.
      const hasVideo = !!section.video_url || !!section.poster_url;
      const hasImg = !!section.image_url && !hasVideo;
      const pos = section.image_pos ?? 'side';
      // Full-bleed background hero: image fills the header, text sits centered on a
      // dark scrim (always white text for legibility, regardless of page style).
      const bgHero = hasImg && pos === 'background';
      const side = section.poster_url ? pos === 'side' : hasImg && !bgHero && pos === 'side';
      const onImg = bgHero; // force white text + translucent badge over the image
      // Hero inline action(s): a primary button + optional secondary ghost link,
      // rendered INSIDE the hero so "headline → button" is one tight unit (no
      // detached cta section floating a full gap below). Same click semantics as
      // the cta section: '#next' advances, '#form' opens the popup, else anchor.
      const heroCtaClick = (label: string, href: string) => (e: React.MouseEvent) => {
        onCtaClick?.(label);
        if (href === '#next') { e.preventDefault(); onNextStep?.(); }
        else if (opensPopup(href)) { e.preventDefault(); onOpenForm?.(); }
      };
      // Editor canvas only: a headline-less hero keeps its headline slot, and a
      // button with a link but no label keeps its button, each showing a
      // placeholder to click and type into. Public pages never set `editable`.
      const headlineSlot = !!section.headline || !!editable;
      const ctaHref = section.cta_href ?? '';
      const ctaSlot = !!(section.cta_label && ctaHref) || (!!editable && !!ctaHref);
      const ctaRow = ctaSlot || (section.cta2_label && section.cta2_href) ? (
        <div className={`flex flex-wrap items-center gap-3 pt-2 ${side ? '' : 'justify-center'}`} style={alignFlex}>
          {ctaSlot && (
            <a href={ctaHref} data-nla-attribution={forwarding} target={ctaHref.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
              onClick={heroCtaClick(section.cta_label ?? '', ctaHref)}
              className={`inline-grid place-items-center ${s.roomy ? 'px-8 py-4 text-[17px]' : 'px-7 py-3.5'} ${s.button}`}
              style={{ background: btnBg, color: onBtn }}>{txt(section.cta_label ?? '', (t) => ({ cta_label: t }))}</a>
          )}
          {section.cta2_label && section.cta2_href && (
            <a href={section.cta2_href} data-nla-attribution={forwarding} target={section.cta2_href.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
              onClick={heroCtaClick(section.cta2_label, section.cta2_href)}
              className={`inline-flex items-center gap-1 px-4 py-3 font-semibold underline-offset-4 hover:underline ${onImg ? 'text-white' : ''}`}
              style={onImg ? undefined : { color: brand }}>{txt(section.cta2_label, (t) => ({ cta2_label: t }))} <span aria-hidden>→</span></a>
          )}
        </div>
      ) : null;
      // Modern styles give a centered headline a wider measure; the
      // image-beside-text hero (`side`) takes its width from the grid column.
      const wide = !!s.roomy && !side;
      // The heading class: white over a background image, else the style's.
      const headingCls = onImg ? `font-bold tracking-tight text-white${design?.heading_color ? ' lp-design-heading' : ''}` : s.heading;
      const text = (
        <div className={`grid gap-4 ${side ? '' : `mx-auto ${wide ? 'max-w-4xl' : 'max-w-2xl'} text-center`}`} style={{ ...(design?.content_width && !side ? { maxWidth: 'none', width: '100%' } : {}), ...(side ? alignText : alignBox) }}>
          {section.logo && theme.logo_url && (
            <img src={theme.logo_url} alt="" loading="lazy" decoding="async" className={`h-11 w-auto object-contain ${side ? '' : 'mx-auto'}`} style={alignMargin} />
          )}
          {section.proof_above && proofLine(!side, onImg)}
          {section.eyebrow && (s.eyebrow
            ? <span className={`justify-self-start ${tz('eyebrow')} ${onImg ? 'font-semibold text-white/80' : s.eyebrow} ${side ? '' : 'justify-self-center'}`} style={alignSelf}>{txt(section.eyebrow, (t) => ({ eyebrow: t }))}</span>
            : <span className={`justify-self-start rounded-full px-3 py-1 ${tz('eyebrow')} font-semibold uppercase tracking-wide ${onImg ? 'bg-white/15 text-white ring-1 ring-white/20' : s.badge} ${side ? '' : 'justify-self-center'}`} style={alignSelf}>
                {txt(section.eyebrow, (t) => ({ eyebrow: t }))}
              </span>
          )}
          {headlineSlot && <h1 className={`${tz('hero_headline')} ${headingCls}`}>{hl(section.headline ?? '', (t) => ({ headline: t }))}</h1>}
          {section.subheadline && <p className={`${tz('hero_sub')} ${onImg ? `text-white/85${design?.text_color ? ' lp-design-text' : ''}` : s.sub}${wide ? ' mx-auto max-w-2xl' : ''}`} style={alignMargin}>{txt(section.subheadline, (t) => ({ subheadline: t }), true)}</p>}
          {ctaRow}
          {ctaNote(!side, onImg)}
          {!section.proof_above && proofLine(!side, onImg)}
        </div>
      );
      if (bgHero) {
        return (
          <header className="relative isolate overflow-hidden rounded-3xl px-6 py-16 shadow-xl sm:px-10 sm:py-24"
            style={{ backgroundImage: `url("${section.image_url}")`, backgroundSize: 'cover', backgroundPosition: design?.background_position ?? 'center' }}>
            <div aria-hidden className="absolute inset-0 -z-10" style={{ background: `rgba(0,0,0,${(section.bg_overlay ?? 45) / 100})` }} />
            {text}
          </header>
        );
      }
      const img = hasImg
        ? <img src={section.image_url} alt="" fetchPriority="high" decoding="async" className={s.media ? `w-full object-cover ${s.media}` : 'w-full rounded-3xl object-cover shadow-xl'} />
        : null;
      const video = section.poster_url
        ? <div className={side ? 'w-full' : 'mx-auto w-full max-w-4xl'} style={side ? undefined : alignMargin}><VideoPoster url={section.video_url} poster={section.poster_url} title={section.headline} frame={s.media ?? 'rounded-3xl shadow-xl'} accent={btnBg} onAccent={onBtn} disabled={preview || editable} /></div>
        : section.video_url
          ? <div className="mx-auto w-full max-w-2xl" style={alignMargin}><EmbedBlock url={section.video_url} title="" /></div>
          : null;
      const media = video ?? img;
      return side
        ? <header className={`grid gap-8 ${stack ? '' : `md:grid-cols-2 md:items-center${s.roomy ? ' md:gap-14' : ''}`}`}>{text}{media}</header>
        : <header className={s.roomy ? 'grid gap-10 sm:gap-12' : 'grid gap-8'}>{text}{media}</header>;
    }
    case 'text': {
      const align = section.align ?? 'left';
      const textAlign = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
      // Editor canvas: a text section with no heading and no body shows its
      // heading slot, so it is never an invisible block.
      const headingSlot = !!section.heading || (!!editable && !section.body);
      return (
        <div className={`mx-auto grid ${s.roomy ? 'max-w-3xl' : 'max-w-2xl'} ${s.roomy ? 'gap-4' : 'gap-2'} ${textAlign}`}>
          {section.eyebrow && s.eyebrow && <span className={`${align === 'center' ? 'mx-auto' : align === 'right' ? 'ml-auto' : ''} ${tz('eyebrow')} ${s.eyebrow}`}>{txt(section.eyebrow, (t) => ({ eyebrow: t }))}</span>}
          {headingSlot && <h2 className={`${tz('heading')} ${s.heading}`}>{hl(section.heading ?? '', (t) => ({ heading: t }))}</h2>}
          {section.body && <p className={`${s.sub} whitespace-pre-line ${tz('body')}`}>{txt(section.body, (t) => ({ body: t }), true)}</p>}
        </div>
      );
    }
    case 'benefits': {
      const align = section.align ?? 'left';
      const justify = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';
      // Indices are kept from the ORIGINAL array (filter happens after pairing)
      // so an inline edit patches the right slot even with blank items around it.
      const all = section.items ?? [];
      const numbered = section.list_style === 'number';
      const patchItem = (i: number) => (t: string) => { const a = [...all]; a[i] = t; return { items: a }; };
      // Editor canvas: an empty list shows a placeholder instead of nothing.
      if (editable && !all.some(Boolean)) {
        const empty = ph('Benefits', 'Add benefits in the panel');
        return section.variant === 'grid' || section.variant === 'cards' ? withHead(empty) : empty;
      }
      if (section.variant === 'grid' || section.variant === 'cards') {
        // Modern checklist: two columns of check rows ('grid'), or each item in
        // its own card ('cards').
        const cards = section.variant === 'cards';
        const rows = all.map((b, i) => ({ b, i })).filter(({ b }) => Boolean(b));
        return withHead(rows.length ? (
          <ul className={`mx-auto grid max-w-5xl gap-3 sm:grid-cols-2 ${cards ? 'sm:gap-4' : 'sm:gap-x-10'}`}>
            {rows.map(({ b, i }, n) => (
              <li key={i} className={`flex items-start gap-3 ${cards ? `p-5 ${s.card}` : ''}`} style={alignment ? { ...alignText, ...alignFlex } : undefined}>
                {numbered
                  ? <span className="mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-full text-xs font-bold" style={{ background: brand, color: onBrand }} aria-hidden>{n + 1}</span>
                  : <span className="mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-full" style={{ background: brand, color: onBrand }} aria-hidden><Check color="currentColor" size={14} /></span>}
                <span className={tz('body')}>{txt(b, patchItem(i))}</span>
              </li>
            ))}
          </ul>
        ) : null);
      }
      return (
        <ul className="mx-auto grid max-w-2xl gap-3">
          {all.map((b, i) => ({ b, i })).filter(({ b }) => Boolean(b)).map(({ b, i }, n) => (
            <li key={i} className={`flex items-start gap-3 ${justify}`} style={alignText}>
              {numbered
                ? <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full text-xs font-bold" style={{ background: `${brand}22`, color: brand }} aria-hidden>{n + 1}</span>
                : <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full text-xs font-bold" style={{ background: brand, color: onBrand }} aria-hidden>✓</span>}
              <span className={tz('body')}>{txt(b, patchItem(i))}</span>
            </li>
          ))}
        </ul>
      );
    }
    case 'stats': {
      const all = section.stats ?? [];
      const stats = all.map((st, i) => ({ st, i })).filter(({ st }) => st.value || st.label);
      const patchStat = (i: number, p: Partial<(typeof all)[number]>) => { const a = [...all]; a[i] = { ...a[i], ...p }; return { stats: a }; };
      if (stats.length && (section.variant === 'cards' || section.variant === 'divided')) {
        const cards = section.variant === 'cards';
        const cols = stats.length >= 4 ? 'grid-cols-2 lg:grid-cols-4' : stats.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2';
        return withHead(
          <div className={`mx-auto grid max-w-6xl ${cols} ${cards ? 'gap-4' : `gap-y-8 sm:divide-x ${s.divider}`}`} {...gridProps}>
            {stats.map(({ st, i }) => (
              <div key={i} className={`text-center ${cards ? `p-6 sm:p-8 ${s.card}` : `px-4 ${s.divider}`}`} style={alignText}>
                <div className={`${tz('stat_value')} leading-none tracking-tight`} style={{ color: brand }}>{txt(st.value, (t) => patchStat(i, { value: t }))}</div>
                <div className={`mt-2.5 ${tz('stat_label')} font-medium ${s.sub}`}>{txt(st.label, (t) => patchStat(i, { label: t }))}</div>
              </div>
            ))}
          </div>,
        );
      }
      return withHead(stats.length ? (
        <div className="mx-auto flex max-w-5xl flex-wrap items-start justify-center gap-x-12 gap-y-8 text-center sm:gap-x-16" {...gridProps}>
          {stats.map(({ st, i }) => (
            <div key={i} className="min-w-[8rem] max-w-[14rem]" style={alignText}>
              <div className={`${tz('stat_value')} leading-none tracking-tight`} style={{ color: brand }}>{txt(st.value, (t) => patchStat(i, { value: t }))}</div>
              <div className={`mt-2 ${tz('stat_label')} font-medium ${s.sub}`}>{txt(st.label, (t) => patchStat(i, { label: t }))}</div>
            </div>
          ))}
        </div>
      ) : ph('Stats bar', 'Add stats in the panel'));
    }
    case 'features': {
      const all = section.features ?? [];
      const feats = all.map((f, i) => ({ f, i })).filter(({ f }) => f.title || f.body);
      const patchFeat = (i: number, p: Partial<(typeof all)[number]>) => { const a = [...all]; a[i] = { ...a[i], ...p }; return { features: a }; };
      // Cap columns to the item count + a max width so 1-2 features don't leave
      // big empty cells in a 3-wide grid. Mobile stays single-column.
      const cols = Math.min(feats.length, 3);
      // Modern styles lay four items out four across (two on tablets) so no
      // card sits alone on a second row; classic styles keep their grid.
      const colClass = s.roomy && feats.length === 4 ? 'sm:grid-cols-2 lg:grid-cols-4 max-w-6xl'
        : cols <= 1 ? 'max-w-md' : cols === 2 ? 'sm:grid-cols-2 max-w-3xl' : 'sm:grid-cols-2 lg:grid-cols-3 max-w-6xl';
      // The feature's mark: a built-in line icon (icon_name), an image, or the
      // classic emoji/number tile. Classic features never set icon_name.
      const mark = (f: (typeof all)[number]): ReactNode => hasPageIcon(f.icon_name)
        ? <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: `${brand}1a`, color: brand, ...alignMargin }} aria-hidden><PageIcon name={f.icon_name} size={22} /></div>
        : f.image
          ? <img src={f.image} alt="" loading="lazy" decoding="async" className="h-12 w-12 object-contain" style={alignMargin} />
          : f.icon
            ? <div className="grid h-11 w-11 place-items-center rounded-xl text-2xl" style={{ background: `${brand}1f`, color: brand, ...alignMargin }} aria-hidden>{f.icon}</div>
            : null;
      if (feats.length && section.variant === 'media') {
        // Image-top cards: the feature image fills the top of the card (an
        // outcome screenshot or photo), then the title and line under it.
        return withHead(
          <div className={`mx-auto grid grid-cols-1 gap-5 ${colClass}`} {...gridProps}>
            {feats.map(({ f, i }) => (
              <div key={i} className={`overflow-hidden ${s.card}`}>
                {f.image
                  ? <img src={f.image} alt="" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" />
                  : <div className="grid aspect-[4/3] w-full place-items-center" style={{ background: `${brand}14`, color: brand }} aria-hidden>{hasPageIcon(f.icon_name) ? <PageIcon name={f.icon_name} size={40} strokeWidth={1.5} /> : null}</div>}
                <div className="p-5 sm:p-6" style={alignText}>
                  {f.title && <h3 className={`${tz('title')} text-lg ${s.heading}`}>{txt(f.title, (t) => patchFeat(i, { title: t }))}</h3>}
                  {f.body && <p className={`mt-1.5 ${tz('small')} ${s.sub}`}>{txt(f.body, (t) => patchFeat(i, { body: t }), true)}</p>}
                </div>
              </div>
            ))}
          </div>,
        );
      }
      if (feats.length && (section.variant === 'plain' || section.variant === 'list')) {
        // 'plain' = no card chrome (icon, title, line); 'list' = icon beside the
        // text in two columns. Both read lighter than a grid of boxes.
        const list = section.variant === 'list';
        return withHead(
          <div className={`mx-auto grid grid-cols-1 ${list ? 'max-w-5xl gap-x-12 gap-y-8 sm:grid-cols-2' : `gap-x-10 gap-y-10 ${colClass}`}`} {...gridProps}>
            {feats.map(({ f, i }) => (
              <div key={i} className={list ? 'flex items-start gap-4' : ''} style={alignment ? { ...alignText, ...(list ? alignFlex : {}) } : undefined}>
                <div className={list ? 'flex-none' : ''}>{mark(f)}</div>
                <div className="min-w-0">
                  {f.title && <h3 className={`${list ? '' : 'mt-4'} ${tz('title')} text-lg ${s.heading}`}>{txt(f.title, (t) => patchFeat(i, { title: t }))}</h3>}
                  {f.body && <p className={`mt-1.5 ${tz('small')} ${s.sub}`}>{txt(f.body, (t) => patchFeat(i, { body: t }), true)}</p>}
                </div>
              </div>
            ))}
          </div>,
        );
      }
      return withHead(feats.length ? (
        <div className={`mx-auto grid grid-cols-1 gap-5 ${colClass}`} {...gridProps}>
          {feats.map(({ f, i }) => (
            <div key={i} className={s.roomy ? `p-6 sm:p-7 ${s.card}` : `rounded-2xl p-6 transition-transform duration-200 hover:-translate-y-1 ${s.card}`} style={alignText}>
              {mark(f)}
              {f.title && <h3 className={`mt-3 ${tz('title')} ${s.heading}`}>{txt(f.title, (t) => patchFeat(i, { title: t }))}</h3>}
              {f.body && <p className={`mt-1 ${tz('small')} ${s.sub}`}>{txt(f.body, (t) => patchFeat(i, { body: t }), true)}</p>}
            </div>
          ))}
        </div>
      ) : ph('Feature grid', 'Add features in the panel'));
    }
    case 'faq': {
      const all = section.faqs ?? [];
      const faqs = all.map((f, i) => ({ f, i })).filter(({ f }) => f.q);
      const patchFaq = (i: number, p: Partial<(typeof all)[number]>) => { const a = [...all]; a[i] = { ...a[i], ...p }; return { faqs: a }; };
      // Editor canvas: every answer stays open so it can be read and edited in
      // place, whether or not the section is selected (opening on select would
      // re-flow the page), and the plus never turns into a cross that reads as
      // a delete button. Public pages keep their closed rows.
      if (faqs.length && (s.roomy || section.variant === 'two-col' || section.variant === 'cards')) {
        // Modern FAQ: a plus that turns into a cross when open, roomier rows,
        // optionally in two columns or as cards.
        const cards = section.variant === 'cards';
        const two = section.variant === 'two-col';
        const item = ({ f, i }: (typeof faqs)[number]) => (
          <details key={i} className={`group ${cards ? `px-5 py-4 sm:px-6 ${s.card}` : `border-b py-5 ${s.divider}`}`} open={editable ? true : undefined}>
            <summary className={`flex cursor-pointer list-none items-start justify-between gap-6 [&::-webkit-details-marker]:hidden ${tz('faq_q')} text-[17px] ${s.heading}`}>
              <span style={alignment ? { ...alignText, flex: 1 } : undefined}>{txt(f.q, (t) => patchFaq(i, { q: t }))}</span>
              <span aria-hidden className={`mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-full text-lg leading-none transition-transform duration-200${editable ? '' : ' group-open:rotate-45'}`} style={{ color: brand }}>+</span>
            </summary>
            {f.a && <p className={`mt-3 pr-10 ${tz('small')} ${s.sub} whitespace-pre-line`} style={alignText}>{txt(f.a, (t) => patchFaq(i, { a: t }), true)}</p>}
          </details>
        );
        if (two) {
          const half = Math.ceil(faqs.length / 2);
          return withHead(
            <div className="mx-auto grid max-w-6xl gap-x-12 md:grid-cols-2">
              <div>{faqs.slice(0, half).map(item)}</div>
              <div>{faqs.slice(half).map(item)}</div>
            </div>,
          );
        }
        return withHead(<div className={`mx-auto max-w-3xl ${cards ? 'grid gap-3' : ''}`}>{faqs.map(item)}</div>);
      }
      return withHead(faqs.length ? (
        <div className="mx-auto max-w-3xl">
          {faqs.map(({ f, i }) => (
            // Editor canvas: render the FAQ open so the answer is editable too
            // (EditableText suppresses the click-to-toggle on the summary).
            <details key={i} className={`border-b py-3 ${s.divider}`} open={editable ? true : undefined}>
              <summary className={`flex cursor-pointer items-center justify-between gap-4 ${tz('faq_q')}`}>
                {alignment ? <span style={{ ...alignText, flex: 1 }}>{txt(f.q, (t) => patchFaq(i, { q: t }))}</span> : txt(f.q, (t) => patchFaq(i, { q: t }))}<span style={{ color: brand }} aria-hidden>+</span>
              </summary>
              {f.a && <p className={`mt-2 ${tz('small')} ${s.sub} whitespace-pre-line`} style={alignText}>{txt(f.a, (t) => patchFaq(i, { a: t }), true)}</p>}
            </details>
          ))}
        </div>
      ) : ph('FAQ', 'Add questions in the panel'));
    }
    case 'steps': {
      const all = section.steps ?? [];
      const steps = all.map((st, i) => ({ st, i })).filter(({ st }) => st.label || st.detail);
      const patchStep = (i: number, p: Partial<(typeof all)[number]>) => { const a = [...all]; a[i] = { ...a[i], ...p }; return { steps: a }; };
      const cols = Math.min(steps.length, 3);
      const colClass = cols <= 1 ? 'max-w-sm' : cols === 2 ? 'sm:grid-cols-2 max-w-2xl' : 'sm:grid-cols-3 max-w-4xl';
      if (steps.length && section.variant === 'cards') {
        const c4 = steps.length >= 4;
        return withHead(
          <div className={`mx-auto grid grid-cols-1 gap-4 ${c4 ? 'sm:grid-cols-2 lg:grid-cols-4 max-w-6xl' : cols === 2 ? 'sm:grid-cols-2 max-w-4xl' : 'sm:grid-cols-3 max-w-6xl'}`} {...gridProps}>
            {steps.map(({ st, i }, k) => (
              <div key={i} className={`p-6 ${s.card}`} style={alignText}>
                <div className="grid h-9 w-9 place-items-center rounded-full text-sm font-bold" style={{ background: brand, color: onBrand, ...alignMargin }}>{k + 1}</div>
                {st.label && <h3 className={`mt-4 ${tz('title')} text-lg ${s.heading}`}>{txt(st.label, (t) => patchStep(i, { label: t }))}</h3>}
                {st.detail && <p className={`mt-1.5 ${tz('small')} ${s.sub}`}>{txt(st.detail, (t) => patchStep(i, { detail: t }), true)}</p>}
              </div>
            ))}
          </div>,
        );
      }
      if (steps.length && section.variant === 'timeline') {
        return withHead(
          <ol className="relative mx-auto grid max-w-2xl gap-8" {...sectionGridProps(design, stack, 'timeline')}>
            <span aria-hidden className="absolute bottom-3 left-[17px] top-3 w-px opacity-25" style={{ background: 'currentColor' }} />
            {steps.map(({ st, i }, k) => (
              <li key={i} className="relative flex items-start gap-5">
                <span className="relative grid h-9 w-9 flex-none place-items-center rounded-full text-sm font-bold" style={{ background: brand, color: onBrand }}>{k + 1}</span>
                <div className="min-w-0 pt-1" style={alignment ? { ...alignText, flex: 1 } : undefined}>
                  {st.label && <h3 className={`${tz('title')} text-lg ${s.heading}`}>{txt(st.label, (t) => patchStep(i, { label: t }))}</h3>}
                  {st.detail && <p className={`mt-1 ${tz('small')} ${s.sub}`}>{txt(st.detail, (t) => patchStep(i, { detail: t }), true)}</p>}
                </div>
              </li>
            ))}
          </ol>,
        );
      }
      return withHead(steps.length ? (
        <div className={`mx-auto grid grid-cols-1 gap-8 ${colClass}`} {...gridProps}>
          {steps.map(({ st, i }, k) => (
            <div key={i} className="text-center" style={alignText}>
              <div className="mx-auto grid h-10 w-10 place-items-center rounded-full font-bold" style={{ background: brand, color: onBrand, ...alignMargin }}>{k + 1}</div>
              {st.label && <h3 className={`mt-3 ${tz('title')} ${s.heading}`}>{txt(st.label, (t) => patchStep(i, { label: t }))}</h3>}
              {st.detail && <p className={`mt-1 ${tz('small')} ${s.sub}`}>{txt(st.detail, (t) => patchStep(i, { detail: t }), true)}</p>}
            </div>
          ))}
        </div>
      ) : ph('Steps', 'Add steps in the panel'));
    }
    case 'logos': {
      const logos = (section.logos ?? []).filter(Boolean);
      if (!logos.length) return withHead(ph('Logo strip', 'Add logo images in the panel'));
      if (s.roomy && !section.logos_marquee) {
        // Modern: the strip's heading is a quiet label, not a section title.
        return (
          <div className="mx-auto grid max-w-6xl justify-items-center gap-6" style={alignGrid}>
            {section.heading && <p className={`text-center text-sm font-medium ${s.label ?? s.sub}`} style={alignText}>{txt(section.heading, (t) => ({ heading: t }))}</p>}
            <div className="flex w-full flex-wrap items-center justify-center gap-x-8 gap-y-5 opacity-80 sm:gap-x-12" style={alignFlex}>
              {logos.map((url, i) => <img key={i} src={url} alt="" loading="lazy" decoding="async" className="h-7 w-auto object-contain sm:h-8" />)}
            </div>
          </div>
        );
      }
      if (section.logos_marquee) {
        // Continuous scroll: the row is rendered twice and the track translates
        // -50%, so the loop is seamless at any logo count. The duplicate row is
        // aria-hidden. prefers-reduced-motion falls back to the static wrap
        // strip via CSS (index.css) — no JS branch needed.
        const row = (dup: boolean) => (
          <div className="page-marquee-row flex shrink-0 items-center gap-x-12 pr-12" {...(dup ? { 'data-dup': '', 'aria-hidden': 'true' } : {})}>
            {logos.map((url, i) => <img key={i} src={url} alt="" loading="lazy" decoding="async" className="h-8 w-auto max-w-none object-contain" />)}
          </div>
        );
        return withHead(
          <div className="mx-auto max-w-5xl overflow-hidden opacity-70" style={{ maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)' }}>
            <div className="page-marquee-track flex w-max items-center" style={{ '--marquee-dur': `${Math.max(16, logos.length * 5)}s` } as React.CSSProperties}>
              {row(false)}
              {row(true)}
            </div>
          </div>,
        );
      }
      return withHead(
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-12 gap-y-8 opacity-70" style={alignFlex}>
          {logos.map((url, i) => <img key={i} src={url} alt="" loading="lazy" decoding="async" className="h-8 w-auto object-contain" />)}
        </div>,
      );
    }
    case 'testimonial': {
      // Editor canvas: a testimonial without a quote keeps its quote slot.
      const quoteSlot = !!section.quote || !!editable;
      if (quoteSlot && section.variant === 'center') {
        // A big centered pull quote with no card: one strong voice.
        return withHead(
          <figure className="mx-auto grid max-w-3xl justify-items-center gap-6 text-center" style={alignGrid}>
            {!!section.rating && <Stars n={section.rating} color={s.star} size={18} />}
            <blockquote className={`text-2xl font-medium leading-snug tracking-tight sm:text-[2rem] sm:leading-[1.3] ${s.heading}`}>“{txt(section.quote ?? '', (t) => ({ quote: t }), true)}”</blockquote>
            {(section.attribution || section.photo_url) && (
              <figcaption className="flex items-center gap-3 text-left" style={alignText}>
                {section.photo_url && <img src={section.photo_url} alt="" loading="lazy" decoding="async" className="h-12 w-12 flex-none rounded-full object-cover" />}
                <span className="text-sm">
                  {section.attribution && <span className={`block font-semibold ${s.heading}`}>{txt(section.attribution, (t) => ({ attribution: t }))}</span>}
                  {section.role && <span className={`block ${s.label ?? s.sub}`}>{txt(section.role, (t) => ({ role: t }))}</span>}
                </span>
              </figcaption>
            )}
          </figure>,
        );
      }
      return withHead(quoteSlot ? (
        <figure className={`relative mx-auto max-w-3xl overflow-hidden rounded-3xl p-8 sm:p-10 ${s.card}`} style={alignText}>
          <span aria-hidden className="pointer-events-none absolute -left-1 -top-5 select-none font-serif text-8xl leading-none opacity-15" style={{ color: brand }}>“</span>
          <blockquote className={`relative ${tz('quote')}`}>{txt(section.quote ?? '', (t) => ({ quote: t }), true)}</blockquote>
          {(section.attribution || section.photo_url) && (
            <figcaption className="mt-4 flex items-center gap-3" style={alignFlex}>
              {section.photo_url && <img src={section.photo_url} alt="" loading="lazy" decoding="async" className="h-11 w-11 flex-none rounded-full object-cover" />}
              <span className={`text-sm ${s.sub}`}>
                {section.attribution && <span className="font-semibold">{txt(section.attribution, (t) => ({ attribution: t }))}</span>}
                {section.role && <span className="block">{txt(section.role, (t) => ({ role: t }))}</span>}
              </span>
            </figcaption>
          )}
        </figure>
      ) : null);
    }
    case 'image':
      if (section.url && s.roomy) return <img src={section.url} alt={section.alt ?? ''} loading="lazy" decoding="async" className={`mx-auto w-full max-w-4xl object-cover ${s.media ?? 'rounded-2xl'}`} style={alignMargin} />;
      return section.url ? <img src={section.url} alt={section.alt ?? ''} loading="lazy" decoding="async" className={s.media ? `w-full object-cover ${s.media}` : 'w-full rounded-3xl object-cover shadow-lg'} style={alignMargin} /> : ph('Image', 'Add an image in the panel');
    case 'pricing': {
      const all = section.plans ?? [];
      const plans = all.map((p, i) => ({ p, i })).filter(({ p }) => p.name || p.price);
      const patchPlan = (i: number, q: Partial<(typeof all)[number]>) => { const a = [...all]; a[i] = { ...a[i], ...q }; return { plans: a }; };
      const cols = Math.min(plans.length, 3);
      const colClass = cols <= 1 ? 'max-w-sm' : cols === 2 ? 'sm:grid-cols-2 max-w-3xl' : 'sm:grid-cols-2 lg:grid-cols-3 max-w-5xl';
      return withHead(plans.length ? (
        <div className={`mx-auto grid grid-cols-1 items-stretch gap-5 pt-3 ${colClass}`} {...gridProps}>
          {plans.map(({ p, i }) => (
            <div key={i} className={`relative flex flex-col rounded-2xl ${s.roomy ? 'p-7 sm:p-8' : 'p-6'} transition-transform duration-200 ${s.card} ${p.highlight ? (s.roomy ? '' : 'shadow-2xl lg:scale-[1.04]') : (s.roomy ? '' : 'hover:-translate-y-1')}`}
              style={{ ...(p.highlight ? (s.roomy ? { borderColor: brand, borderWidth: 2 } : { borderColor: brand, borderWidth: 2, boxShadow: `0 20px 45px -20px ${brand}80` }) : {}), ...alignText }}>
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-0.5 text-xs font-semibold"
                  style={{ background: brand, color: onBrand }}>{p.badge || 'Most popular'}</span>
              )}
              {p.name && <h3 className={`${tz('title')} ${s.heading}`}>{txt(p.name, (t) => patchPlan(i, { name: t }))}</h3>}
              {p.description && <p className={`mt-1 text-sm ${s.sub}`}>{txt(p.description, (t) => patchPlan(i, { description: t }))}</p>}
              <div className="mt-2 flex items-baseline gap-1" style={alignFlex}>
                {p.price_compare && <s className={`mr-1.5 text-lg ${s.label ?? s.sub}`}>{p.price_compare}</s>}
                <span className={`${tz('price')} ${s.heading}`}>{txt(p.price, (t) => patchPlan(i, { price: t }))}</span>
                {p.period && <span className={`text-sm ${s.sub}`}>{txt(p.period, (t) => patchPlan(i, { period: t }))}</span>}
              </div>
              <ul className="mt-4 grid flex-1 content-start gap-2 text-sm">
                {(p.features ?? []).filter(Boolean).map((f, k) => (
                  <li key={k} className="flex items-start gap-2" style={alignFlex}>
                    {s.roomy
                      ? <Check color={brand} size={16} className="mt-[2px]" />
                      : <span className="mt-0.5 font-bold" style={{ color: brand }} aria-hidden>✓</span>}
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
              {p.cta_label && (
                <a href={p.cta_href || '#form'} data-nla-attribution={forwarding} target={(p.cta_href || '#form').startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
                  onClick={(e) => { onCtaClick?.(p.cta_label!); const h = p.cta_href || '#form'; if (h === '#next') { e.preventDefault(); onNextStep?.(); } else if (opensPopup(h)) { e.preventDefault(); onOpenForm?.(); } }}
                  className={`mt-5 inline-grid place-items-center ${s.roomy ? 'px-5 py-3.5' : 'px-5 py-2.5'} ${s.button}`}
                  style={s.roomy && !p.highlight && !design?.button_color ? { background: 'transparent', color: 'inherit', boxShadow: 'inset 0 0 0 1.5px currentColor' } : { background: btnBg, color: onBtn }}>{p.cta_label}</a>
              )}
            </div>
          ))}
        </div>
      ) : ph('Pricing', 'Add plans in the panel'));
    }
    case 'video':
      if (section.poster_url) {
        return (
          <div className="mx-auto grid w-full max-w-4xl gap-6" style={alignMargin}>
            {section.heading && <h2 className={`text-center ${tz('heading')} ${s.heading}`} style={alignText}>{hl(section.heading, (t) => ({ heading: t }))}</h2>}
            <VideoPoster url={section.video_url} poster={section.poster_url} title={section.heading} frame={s.media ?? 'rounded-3xl shadow-xl'} accent={btnBg} onAccent={onBtn} disabled={preview || editable} />
          </div>
        );
      }
      return section.video_url ? (
        <div className="mx-auto grid w-full max-w-3xl gap-5" style={alignMargin}>
          {section.heading && <h2 className={`text-center text-2xl sm:text-3xl ${s.heading}`} style={alignText}>{section.heading}</h2>}
          <EmbedBlock url={section.video_url} title={section.heading || 'Video'} />
        </div>
      ) : ph('Video', 'Paste a YouTube, Loom, Vimeo, or Wistia URL in the panel');
    case 'gallery': {
      const images = (section.images ?? []).filter(Boolean);
      const grid = images.length <= 1 ? 'max-w-xl' : images.length === 2 ? 'grid-cols-2 max-w-3xl' : 'grid-cols-2 sm:grid-cols-3 max-w-5xl';
      return withHead(images.length ? (
        <div className={`mx-auto grid w-full gap-4 ${grid}`} {...gridProps}>
          {images.map((url, i) => (
            <img key={i} src={url} alt="" loading="lazy" decoding="async" className="aspect-square w-full rounded-2xl object-cover shadow-sm" />
          ))}
        </div>
      ) : ph('Gallery', 'Add images in the panel'));
    }
    case 'team': {
      const members = (section.members ?? []).filter((m) => m.name);
      if (members.length && s.roomy) {
        // Modern: portrait cards (photo on top, name + role under it).
        return withHead(
          <div className={`mx-auto grid grid-cols-2 gap-4 sm:gap-6 ${members.length >= 4 ? 'lg:grid-cols-4 max-w-6xl' : members.length === 3 ? 'sm:grid-cols-3 max-w-5xl' : 'max-w-2xl'}`} {...gridProps}>
            {members.map((m, i) => (
              <figure key={i} className="min-w-0" style={alignText}>
                {m.photo_url
                  ? <img src={m.photo_url} alt="" loading="lazy" decoding="async" className={`aspect-[4/5] w-full object-cover ${s.media ?? 'rounded-2xl'}`} />
                  : <div className={`grid aspect-[4/5] w-full place-items-center text-4xl font-bold ${s.media ?? 'rounded-2xl'}`} style={{ background: brand, color: onBrand }} aria-hidden>{m.name.trim().charAt(0).toUpperCase()}</div>}
                <figcaption className="mt-3">
                  <div className={`font-semibold ${s.heading}`}>{m.name}</div>
                  {m.role && <div className={`text-sm ${s.label ?? s.sub}`}>{m.role}</div>}
                </figcaption>
              </figure>
            ))}
          </div>,
        );
      }
      return withHead(members.length ? (
        <div className="mx-auto flex max-w-5xl flex-wrap justify-center gap-x-12 gap-y-10 text-center" {...gridProps}>
          {members.map((m, i) => (
            <div key={i} className="w-32" style={alignText}>
              {m.photo_url
                ? <img src={m.photo_url} alt="" loading="lazy" decoding="async" className="mx-auto h-20 w-20 rounded-full object-cover shadow" style={alignMargin} />
                : <div className="mx-auto grid h-20 w-20 place-items-center rounded-full text-2xl font-bold" style={{ background: brand, color: onBrand, ...alignMargin }} aria-hidden>{m.name.trim().charAt(0).toUpperCase()}</div>}
              <div className={`mt-3 font-semibold ${s.heading}`}>{m.name}</div>
              {m.role && <div className={`text-sm ${s.sub}`}>{m.role}</div>}
            </div>
          ))}
        </div>
      ) : ph('Team', 'Add people in the panel'));
    }
    case 'guarantee': {
      // Editor canvas: an empty guarantee keeps its heading slot.
      const headingSlot = !!section.heading || (!!editable && !section.body);
      if ((headingSlot || section.body) && s.roomy) {
        // Modern: a seal beside the promise, no tinted border.
        return (
          <div className={`mx-auto flex max-w-3xl flex-col items-center gap-5 p-6 text-center sm:flex-row sm:items-center sm:gap-7 sm:p-8 sm:text-left ${s.card}`} style={alignText}>
            <span className="grid h-16 w-16 flex-none place-items-center rounded-full" style={{ background: `${brand}1a`, color: brand }} aria-hidden>
              <PageIcon name="shield" size={30} />
            </span>
            <div className="grid gap-1.5" style={alignment ? { flex: 1 } : undefined}>
              {headingSlot && <h3 className={`text-xl sm:text-2xl ${s.heading}`}>{hl(section.heading ?? '', (t) => ({ heading: t }))}</h3>}
              {section.body && <p className={`${tz('small')} ${s.sub} whitespace-pre-line`}>{txt(section.body, (t) => ({ body: t }), true)}</p>}
            </div>
          </div>
        );
      }
      return (headingSlot || section.body) ? (
        <div className={`mx-auto grid max-w-xl gap-2 rounded-2xl p-6 text-center ${s.card}`}
          style={{ borderColor: 'rgba(16, 185, 129, 0.45)', ...alignText }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1.75"
            strokeLinecap="round" strokeLinejoin="round" className="mx-auto" style={alignMargin} aria-hidden="true">
            <path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10Z" />
            <path d="m8.8 11.8 2.3 2.3 4.3-4.8" />
          </svg>
          {headingSlot && <h3 className={`${tz('guarantee_heading')} ${s.heading}`}>{txt(section.heading ?? '', (t) => ({ heading: t }))}</h3>}
          {section.body && <p className={`${tz('small')} ${s.sub} whitespace-pre-line`}>{txt(section.body, (t) => ({ body: t }), true)}</p>}
        </div>
      ) : null;
    }
    case 'divider': {
      const pad = section.size === 'sm' ? 'py-1' : section.size === 'lg' ? 'py-12' : 'py-5';
      return (
        <div className={pad} aria-hidden>
          {section.line && <hr className={`mx-auto max-w-md border-t ${s.divider}`} />}
        </div>
      );
    }
    case 'countdown':
      // Editor canvas: a countdown with no end date yet shows a placeholder.
      return editable && !Number.isFinite(deadlineTime(section.deadline))
        ? ph('Countdown', 'Pick an end date in the panel')
        : <CountdownBlock section={section} s={s} brand={brand} />;
    case 'embed':
      return section.embed_url ? <EmbedBlock url={section.embed_url} /> : ph('Embed', 'Paste an embed link or iframe code in the panel');
    case 'booking': {
      // A CRM calendar (/book/<calendar>?embed=1) on the page's own host.
      // id="book" so a CTA can deep-scroll to the calendar (href '#book'), like '#form'.
      const slug = (section.booking_slug ?? '').trim();
      if (!slug) return ph('Booking calendar', 'Pick a booking calendar in the panel');
      const frame = <BookingFrame slug={slug} onBooked={onBooked} />;
      return s.roomy && sectionHead
        ? <div id="book">{sectionHead}{frame}</div>
        : <div id="book">{frame}</div>;
    }
    case 'cta': {
      // A click handler shared by the single button and the stacked link list.
      const ctaClick = (label: string, href: string) => (e: React.MouseEvent) => {
        onCtaClick?.(label);
        if (href === '#next') { e.preventDefault(); onNextStep?.(); }
        else if (opensPopup(href)) { e.preventDefault(); onOpenForm?.(); }
      };
      // Linktree-style stacked link buttons: when the section has a `links` list,
      // render a tight column of full-width pills (centered, capped width) instead
      // of one button. Editable via the CTA inspector's link list.
      const links = (section.links ?? []).filter((l) => l.label);
      if (links.length) {
        return (
          <div className="mx-auto grid w-full max-w-md gap-3" style={alignMargin}>
            {links.map((l, i) => {
              const href = l.href || '#';
              return (
                <a key={i} href={href} data-nla-attribution={forwarding} target={href.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
                  onClick={ctaClick(l.label, href)}
                  className={`block w-full px-6 py-3.5 text-center ${tz('cta') ? `${tz('cta')} ` : ''}${s.button}`}
                  style={{ background: btnBg, color: onBtn }}>{l.label}</a>
              );
            })}
          </div>
        );
      }
      // Editor canvas: a button with no label yet still shows, with a "Button
      // text" placeholder to click and type into. Public pages never set
      // `editable`, so there a button needs both a label and a link, as always.
      const href = section.href || '#form';
      const button = !!(section.label && section.href) || (!!editable && !section.label);
      // A closing call-to-action block: heading + line + button + note/proof.
      // Only when one of those optional fields is set; a bare button is unchanged.
      if (button && (section.heading || section.subheadline || section.cta_note || section.proof_text || section.rating)) {
        return (
          <div className={`mx-auto grid max-w-3xl justify-items-center ${s.roomy ? 'gap-5' : 'gap-4'} text-center`} style={alignment ? { ...alignGrid, ...alignMargin } : undefined}>
            {section.eyebrow && <span className={`${tz('eyebrow')} ${eyebrowCls}`}>{txt(section.eyebrow, (t) => ({ eyebrow: t }))}</span>}
            {section.heading && <h2 className={`${tz('heading')} ${s.heading}`}>{hl(section.heading, (t) => ({ heading: t }))}</h2>}
            {section.subheadline && <p className={`max-w-2xl ${tz('hero_sub')} ${s.sub}`}>{txt(section.subheadline, (t) => ({ subheadline: t }), true)}</p>}
            <a href={href} data-nla-attribution={forwarding} target={href.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
              onClick={ctaClick(section.label ?? '', href)}
              className={`mt-1 inline-grid place-items-center ${s.roomy ? 'px-8 py-4 text-[17px]' : 'px-7 py-3.5'} ${tz('cta') ? `${tz('cta')} ` : ''}${s.button}`}
              style={{ background: btnBg, color: onBtn }}>{txt(section.label ?? '', (t) => ({ label: t }))}</a>
            {ctaNote(true)}
            {proofLine(true)}
          </div>
        );
      }
      // href '#next' advances a multi-step page (the parent owns the logic via
      // onNextStep — including the last-step fallback to the form).
      return button ? (
        <div className="text-center" style={alignText}>
          <a href={href} data-nla-attribution={forwarding} target={href.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
            onClick={ctaClick(section.label ?? '', href)}
            className={`inline-grid place-items-center px-7 py-3.5 ${tz('cta') ? `${tz('cta')} ` : ''}${s.button}`}
            style={{ background: btnBg, color: onBtn }}>{txt(section.label ?? '', (t) => ({ label: t }))}</a>
        </div>
      ) : null;
    }
    case 'form': {
      const layout = section.layout === 'split' || section.layout === 'stacked' ? section.layout : 'card';
      const width = section.form_width === 'wide' || section.form_width === 'full' ? section.form_width : 'standard';
      const wideSplit = layout === 'split' && section.form_ratio === 'wide';
      const expanded = layout === 'split' ? wideSplit : width !== 'standard';
      const cardWidth = width === 'wide' ? 'max-w-4xl' : width === 'full' ? 'max-w-none' : 'max-w-xl';
      // The page's CRM form, live in a frame; a still of the template's form in
      // galleries (previewForm); on the editor canvas with no form, a note on
      // what to do. A public page with no form shows no card at all.
      const formAccent = formAccentFor(theme);
      const inner: ReactNode = form
        ? <CrmFormFrame slug={form.slug} name={form.name} pageId={pageId} accent={formAccent}
            expanded={expanded || undefined} fieldDesign={section.form_fields} preview={formPreview} blocked={!!editable || !!preview} onDone={onFormDone} onBooked={onBooked} />
        : previewForm
          ? <FormMock title={previewForm.title} description={previewForm.description} fields={previewForm.fields} accent={formAccent} expanded={expanded || undefined} fieldDesign={section.form_fields} />
          : null;
      // The card keeps each style's frame (border, corners, shadow); the form
      // inside brings its own spacing, so the frame's padding is dropped.
      const frameCls = editorial
        ? 'relative w-full overflow-hidden rounded-[24px] border p-6 pt-8 shadow-[0_18px_55px_-38px_rgba(20,19,24,0.42)] sm:p-8 sm:pt-9'
        : s.roomy ? (s.formCard ?? 'w-full rounded-2xl border p-6 shadow-[0_24px_60px_-34px_rgba(0,0,0,0.35)] sm:p-8') : 'w-full rounded-3xl border p-6 shadow-2xl sm:p-7';
      const formCard: ReactNode = inner ? (
        <div id="form" className={flushFrame(`${frameCls} relative overflow-hidden`)}
          style={{ background: formBgCss(theme), borderColor: s.formBorder }}>
          {editorial && <EditorialMotif />}
          {inner}
        </div>
      ) : editable ? ph('No form yet', 'Pick a form in the Section tab.') : null;
      // Popup mode: the form lives in a modal (owned by PageView). Here we render
      // only the button that opens it; CTAs pointing to '#form' open it too. A
      // public page with no form shows no button.
      if (popup) {
        if (!form && !previewForm && !editable) return null;
        const label = section.label || 'Get started';
        return (
          <div id="form" className="text-center" style={alignText}>
            <button type="button" onClick={() => onOpenForm?.()} disabled={preview || (!form && !previewForm)}
              className={`inline-grid place-items-center px-7 py-3.5 ${tz('cta') ? `${tz('cta')} ` : ''}${s.button}`}
              style={{ background: btnBg, color: onBtn }}>{txt(label, (t) => ({ label: t }))}</button>
          </div>
        );
      }
      // Split layout: marketing text on one side, the form card on the other.
      // Text is always first in the DOM (mobile stacks text above form); on desktop
      // form_side moves the form left/right via order utilities.
      if (layout === 'split' || layout === 'stacked') {
        const all = section.items ?? [];
        const items = all.map((b, i) => ({ b, i })).filter(({ b }) => Boolean(b));
        const formLeft = section.form_side === 'left';
        const textCol = (
          <div className={`grid content-center gap-4${layout === 'stacked' ? ' mx-auto w-full max-w-3xl text-center' : ''}`} style={layout === 'stacked' ? alignBox : alignText}>
            {section.eyebrow && s.eyebrow && <span className={`${layout === 'stacked' ? 'justify-self-center' : 'justify-self-start'} ${tz('eyebrow')} ${s.eyebrow}`} style={alignSelf}>{txt(section.eyebrow, (t) => ({ eyebrow: t }))}</span>}
            {section.headline && <h2 className={`${tz('heading')} ${s.heading}`}>{hl(section.headline, (t) => ({ headline: t }))}</h2>}
            {section.subheadline && <p className={`${tz('body')} ${s.sub} whitespace-pre-line`}>{txt(section.subheadline, (t) => ({ subheadline: t }), true)}</p>}
            {items.length > 0 && (
              <ul className={`grid gap-3${layout === 'stacked' ? ' justify-self-center text-left' : ''}`} style={alignment ? { ...alignText, ...alignSelf } : undefined}>
                {items.map(({ b, i }) => (
                  <li key={i} className="flex items-start gap-3" style={alignFlex}>
                    <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full text-xs font-bold" style={{ background: brand, color: onBrand }} aria-hidden>✓</span>
                    <span className={tz('body')}>{txt(b, (t) => { const a = [...all]; a[i] = t; return { items: a }; })}</span>
                  </li>
                ))}
              </ul>
            )}
            {proofLine(layout === 'stacked')}
          </div>
        );
        if (layout === 'stacked') {
          const hasText = !!section.headline || !!section.subheadline || !!(section.eyebrow && s.eyebrow) || items.length > 0 || !!section.proof_text || !!section.proof_avatars?.some(Boolean) || !!section.rating;
          if (!hasText && !formCard) return null;
          return (
            <div className="grid gap-8">
              {hasText && textCol}
              {formCard && <div className={`mx-auto w-full ${cardWidth}`}>{formCard}</div>}
            </div>
          );
        }
        if (!formCard) return <div className="mx-auto max-w-2xl">{textCol}</div>;
        const columns = stack ? '' : wideSplit
          ? formLeft ? 'md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : 'md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]'
          : twoCol;
        return (
          <div className={`grid items-center gap-8 ${columns}${s.roomy && !stack ? ' md:gap-14' : ''}`}>
            <div className={ord(formLeft ? 'md:order-2' : '')}>{textCol}</div>
            <div className={ord(formLeft ? 'md:order-1' : '')}>{formCard}</div>
          </div>
        );
      }
      // Default: the centered card.
      return formCard ? <div className={`mx-auto w-full ${cardWidth}`}>{formCard}</div> : null;
    }
    case 'split': {
      // Editor canvas: with no text yet the heading slot shows, and a missing
      // image shows a placeholder, so the block is never empty space.
      const headingSlot = !!section.heading || (!!editable && !section.body && !section.eyebrow);
      if (!section.image_url && !headingSlot && !section.body && !section.eyebrow) return null;
      const imgLeft = (section.image_side ?? 'left') === 'left';
      const img = section.image_url
        ? <img src={section.image_url} alt={section.alt ?? ''} fetchPriority="high" decoding="async" className={s.media ? `w-full object-cover ${s.media}` : 'w-full rounded-3xl object-cover shadow-lg'} />
        : ph('Image', 'Add an image in the panel');
      const splitHref = section.href ?? '';
      const button = !!(section.label && splitHref) || (!!editable && !!splitHref);
      const all = section.items ?? [];
      const items = all.map((b, i) => ({ b, i })).filter(({ b }) => Boolean(b));
      const textCol = (
        <div className="grid content-center gap-4" style={alignText}>
          {section.eyebrow && (s.eyebrow
            ? <span className={`justify-self-start ${tz('eyebrow')} ${s.eyebrow}`} style={alignSelf}>{txt(section.eyebrow, (t) => ({ eyebrow: t }))}</span>
            : <span className={`justify-self-start rounded-full px-3 py-1 ${tz('eyebrow')} font-semibold uppercase tracking-wide ${s.badge}`} style={alignSelf}>{txt(section.eyebrow, (t) => ({ eyebrow: t }))}</span>)}
          {headingSlot && <h2 className={`${tz('heading')} ${s.heading}`}>{hl(section.heading ?? '', (t) => ({ heading: t }))}</h2>}
          {section.body && <p className={`${tz('body')} ${s.sub} whitespace-pre-line`}>{txt(section.body, (t) => ({ body: t }), true)}</p>}
          {items.length > 0 && (
            <ul className="grid gap-2.5">
              {items.map(({ b, i }) => (
                <li key={i} className={`flex items-start gap-2.5 ${tz('body')}`} style={alignFlex}>
                  <Check color={brand} className="mt-[3px]" />
                  <span>{txt(b, (t) => { const a = [...all]; a[i] = t; return { items: a }; })}</span>
                </li>
              ))}
            </ul>
          )}
          {button && (
            <a href={splitHref} data-nla-attribution={forwarding} target={splitHref.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
              onClick={(e) => { onCtaClick?.(section.label ?? ''); if (splitHref === '#next') { e.preventDefault(); onNextStep?.(); } else if (opensPopup(splitHref)) { e.preventDefault(); onOpenForm?.(); } }}
              className={`inline-grid justify-self-start place-items-center px-6 py-3 ${s.button}`}
              style={{ background: btnBg, color: onBtn, ...alignSelf }}>{txt(section.label ?? '', (t) => ({ label: t }))}</a>
          )}
          {ctaNote(false)}
        </div>
      );
      // Image always first in the DOM (mobile: image on top); desktop side via order.
      return (
        <div className={`grid items-center gap-8 ${twoCol}${s.roomy && !stack ? ' md:gap-14' : ''}`}>
          <div className={ord(imgLeft ? '' : 'md:order-2')}>{img}</div>
          <div className={ord(imgLeft ? '' : 'md:order-1')}>{textCol}</div>
        </div>
      );
    }
    case 'footer': {
      const links = (section.links ?? []).filter((l) => l.label && l.href);
      // Editor canvas: an empty footer keeps its text slot.
      const bodySlot = !!section.body || (!!editable && links.length === 0);
      if (!bodySlot && links.length === 0) return null;
      const body = bodySlot ? <p className="whitespace-pre-line">{txt(section.body ?? '', (t) => ({ body: t }), true, 'Add footer text')}</p> : null;
      const footerLayout = section.footer_layout === 'row' || section.footer_layout === 'centered' ? section.footer_layout : undefined;
      if (footerLayout === 'row' || (!footerLayout && s.roomy)) {
        // Modern: one quiet row (copyright left, links right), stacked on phones.
        return (
          <footer className={`flex flex-col items-center justify-between gap-3 border-t pt-8 text-center text-sm sm:flex-row sm:text-left ${s.label ?? s.sub}`} style={{ borderColor: s.formBorder, ...alignText }}>
            {body}
            {links.length > 0 && (
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-1" style={alignFlex}>
                {links.map((l, i) => (
                  <a key={i} href={l.href} target={l.href.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
                    className="transition-opacity hover:opacity-70">{l.label}</a>
                ))}
              </div>
            )}
          </footer>
        );
      }
      return (
        <footer className={`mt-2 border-t ${s.roomy ? 'pt-8' : 'pt-6'} text-center text-sm ${s.sub}`} style={{ borderColor: s.formBorder, ...alignText }}>
          {body}
          {links.length > 0 && (
            <div className="mt-2 flex flex-wrap justify-center gap-x-5 gap-y-1" style={alignFlex}>
              {links.map((l, i) => (
                <a key={i} href={l.href} target={l.href.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer"
                  className="hover:underline" style={{ color: brand }}>{l.label}</a>
              ))}
            </div>
          )}
        </footer>
      );
    }
    case 'compare': {
      const col = (title: string | undefined, items: string[] | undefined, key: 'left_items' | 'right_items', titleKey: 'left_title' | 'right_title', side: 'left' | 'right') => {
        const isRight = side === 'right';
        const all = items ?? [];
        const rows = all.map((it, i) => ({ it, i })).filter(({ it }) => Boolean(it));
        if (s.roomy) {
          // Modern: the "yes" column carries the brand ring; marks are drawn icons.
          return (
            <div key={side} className={`p-6 sm:p-8 ${s.card}`} style={{ ...(isRight ? { borderColor: brand, borderWidth: 2 } : {}), ...alignText }}>
              {title && <div className={`mb-4 text-lg font-semibold ${isRight ? s.heading : s.sub}`}>{txt(title, (t) => ({ [titleKey]: t }))}</div>}
              <ul className="grid gap-3">
                {rows.map(({ it, i }) => (
                  <li key={i} className="flex items-start gap-3" style={alignFlex}>
                    {isRight
                      ? <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full" style={{ background: brand, color: onBrand }} aria-hidden><Check color="currentColor" size={12} /></span>
                      : <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full opacity-45 ring-1 ring-current" aria-hidden><PageIcon name="x" size={11} strokeWidth={2.5} /></span>}
                    <span className={`${tz('body')} ${isRight ? '' : s.sub}`}>{txt(it, (t) => { const a = [...all]; a[i] = t; return { [key]: a }; })}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return (
          <div key={side} className={`rounded-2xl p-6 ${s.card}`} style={alignText}>
            {title && (
              <div className={`mb-3 ${tz('title')} uppercase tracking-wide ${isRight ? '' : s.sub}`} style={isRight ? { color: brand } : undefined}>
                {txt(title, (t) => ({ [titleKey]: t }))}
              </div>
            )}
            <ul className="grid gap-2">
              {rows.map(({ it, i }) => (
                <li key={i} className="flex items-start gap-2" style={alignFlex}>
                  <span aria-hidden className={isRight ? '' : 'opacity-50'} style={isRight ? { color: brand } : undefined}>{isRight ? '✓' : '✕'}</span>
                  <span className={tz('body')}>{txt(it, (t) => { const a = [...all]; a[i] = t; return { [key]: a }; })}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      };
      return withHead(
        <div className={`mx-auto grid w-full ${s.roomy ? 'max-w-4xl gap-5 md:grid-cols-2' : 'max-w-3xl gap-4 sm:grid-cols-2'}`}>
          {col(section.left_title, section.left_items, 'left_items', 'left_title', 'left')}
          {col(section.right_title, section.right_items, 'right_items', 'right_title', 'right')}
        </div>
      );
    }
    case 'nav': return <NavBlock k={kit()} />;
    case 'banner': return <BannerBlock k={kit()} />;
    case 'testimonials': return <TestimonialsBlock k={kit()} />;
    case 'modules': return <ModulesBlock k={kit()} />;
    case 'bio': return <BioBlock k={kit()} />;
    case 'offer': return <OfferBlock k={kit()} />;
    default:
      return null;
  }
  };
  return widthNode(renderSection());
}

/** Live d/h/m/s countdown to section.deadline. Ticks every second (interval is
 *  cleaned up on unmount). Past the deadline it shows expired_text, or nothing.
 *  Invalid/empty deadline renders nothing. */
function CountdownBlock({ section, s, brand }: { section: Section; s: StyleClasses; brand: string }) {
  const target = deadlineTime(section.deadline);
  // A server-rendered page can't know the visitor's clock (its HTML may come
  // from the CDN hours later), so the server HTML and the hydration render show
  // "--" and the real time lands right after mount.
  const ssr = useContext(SsrContext);
  const [now, setNow] = useState<number | null>(() => (ssr ? null : Date.now()));
  useEffect(() => {
    if (!Number.isFinite(target)) return;
    setNow((n) => n ?? Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!Number.isFinite(target)) return null;
  const left = now === null ? null : target - now;
  if (left !== null && left <= 0) {
    return section.expired_text
      ? <p className={`text-center text-lg ${s.sub}`}>{section.expired_text}</p>
      : null;
  }
  const boxes: [number | null, string][] = left === null
    ? [[null, 'days'], [null, 'hours'], [null, 'min'], [null, 'sec']]
    : [
      [Math.floor(left / 86_400_000), 'days'],
      [Math.floor(left / 3_600_000) % 24, 'hours'],
      [Math.floor(left / 60_000) % 60, 'min'],
      [Math.floor(left / 1_000) % 60, 'sec'],
    ];
  return (
    <div className="grid gap-5 text-center">
      {section.heading && <h2 className={`text-2xl sm:text-3xl ${s.heading}`}>{section.heading}</h2>}
      <div className="flex justify-center gap-2.5 sm:gap-3">
        {boxes.map(([v, label]) => (
          <div key={label} className={`w-16 rounded-xl py-3 sm:w-20 ${s.card}`}>
            <div className="text-2xl font-extrabold tabular-nums sm:text-3xl" style={{ color: brand }}>{v === null ? '--' : String(v).padStart(2, '0')}</div>
            <div className={`mt-0.5 text-[11px] ${s.roomy ? '' : 'uppercase tracking-wide '}${s.sub}`}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
